import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useToast } from "../../store/ToastProvider";
import {
  UploadedFiles,
  useUploadedFiles,
} from "../../store/UploadedFileProvider";
import { useUser } from "../../store/UserProvider";
import { NavBar } from "../../components/common/NavBar";
import { SideMenu } from "../../components/common/SideMenu";
import { Status } from "../../enums/Status";
import { FileLoadingCard } from "./FileLoadingCard";
import { SuccessFulUploadBox } from "./SuccessFulUploadBox";
import { FailedUploadBox } from "./FailedUploadBox";
import { MagicLinkModal } from "./MagicLinkModal";
import { usePost } from "../../hooks/usePost";
import {
  FileLocation,
  FileOptionTabTitle,
  UploadFileRequestParams,
} from "../../types/fileUploadTypes";
import { ValidationResponse } from "../../types/validationTypes";

import {
  BackgroundContainer,
  MainColumn as Column,
  NavBarContainer,
  RowContainer,
  Card,
  ElementContainer,
  BoxWithShadow,
  FlexibleContainer,
} from "../../components/containers/styledComponents";
import {
  CompassMainHeader,
  CompassParagraph,
} from "../../components/common/styledComponents";
import { validateFile } from "./utils";
import { MESSAGES } from "../../messages";
import { greyText, compassLightGreen } from "../../styles";
import { Icon, IconContainer } from "../../components/visuals/styledComponents";
import { PageButton } from "../../components/visuals/PageButton";
import fileUploadIcon from "../../assets/svg/file_upload.svg";
import linkIcon from "../../assets/svg/link.svg";
import {
  TabElement,
  TabOptions,
  findActiveTab,
} from "../../components/visuals/TabOptions/TabOptions";
import { toSlug as slugify } from "../../utils/formattingUtils";
import {
  FILE_OPTION_ROUTES_MAPPING,
  FILE_OPTIONS,
  getRoleUIConfig,
} from "./constants";
import { ROUTES } from "../../routes/routeNames";

const AddFileIconContainer = styled(IconContainer)<{
  isDragOver: boolean;
}>`
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
  &:hover {
    transform: scale(1.1);
  }
  ${({ isDragOver }): string | false =>
    isDragOver &&
    `
  transform: scale(1.1);  
`}
`;

const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  opacity: 0;
  pointer-events: none;
`;

// To handle drag over events
const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
  event.preventDefault();
};

export const AddFile = (): JSX.Element => {
  const { addToast } = useToast();
  const { fileType } = useParams();
  const navigate = useNavigate();
  const { isRestrictedUser } = useUser();
  const roleConfig = getRoleUIConfig(isRestrictedUser);
  const [showMagicLinkModal, setShowMagicLinkModal] = useState(false);

  const {
    sendData: uploadFile,
    error: uploadError,
    data: fileUploadedLocation,
  } = usePost<FileLocation, UploadFileRequestParams>();
  const { uploadedFiles, setUploadedFiles } = useUploadedFiles();
  const [show, setShow] = useState<boolean>(false);
  const [fileUploadStatus, setFileUploadStatus] = useState<Status>(
    Status.PENDING,
  );

  // Filter file options based on role config
  const allowedFileOptions = useMemo(
    () =>
      FILE_OPTIONS.filter((option) =>
        roleConfig.allowedFileTypes.includes(
          option.title as FileOptionTabTitle,
        ),
      ),
    [roleConfig.allowedFileTypes],
  );
  const [tabElements, setTabElements] = useState(allowedFileOptions);
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [overrideFileType, setOverrideFileType] =
    useState<FileOptionTabTitle | null>(null);
  const [failedValidationResponses, setFailedValidationResponses] = useState<
    Partial<Record<FileOptionTabTitle, ValidationResponse>>
  >({});
  const [lastUploadedFilenames, setLastUploadedFilenames] = useState<
    Partial<Record<FileOptionTabTitle, string>>
  >({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeTab: TabElement = useMemo(
    () => findActiveTab(tabElements),
    [tabElements, overrideFileType],
  );

  useEffect(() => {
    if (fileType) {
      const overrideTabTitle = tabElements.find(
        (tab) => slugify(tab.title) === fileType,
      )?.title;
      if (overrideTabTitle) {
        setOverrideFileType(overrideTabTitle as FileOptionTabTitle);
      }
    }
  }, [fileType, tabElements]);

  useEffect(() => {
    if (activeTab.title !== overrideFileType) {
      navigate(`${ROUTES.ADD_FILE}/${slugify(activeTab.title)}`);
    }
  }, [activeTab]);

  useEffect(() => {
    if (file) {
      uploadFile(
        FILE_OPTION_ROUTES_MAPPING[activeTab.title as FileOptionTabTitle]
          .upload,
        { file: file },
        {},
        true,
      );
    }
  }, [file]);

  // Store file location after successful upload (no auto-validation)
  useEffect(() => {
    if (fileUploadedLocation && file) {
      setUploadedFiles((prevUploadedFiles: UploadedFiles) => ({
        ...prevUploadedFiles,
        [activeTab.title]: {
          file: file,
          status: Status.SUCCESS,
          location: fileUploadedLocation,
        },
      }));
      setFileUploadStatus(Status.SUCCESS);
      setFile(null);
      setFileUploadStatus(Status.PENDING);
    }
  }, [fileUploadedLocation]);

  useEffect(() => {
    if (uploadError) {
      handleFileUploadFailure();
    }
  }, [uploadError]);

  const triggerFileInput = (): void => {
    fileInputRef.current?.click();
  };

  const handleFileUploadFailure = (): void => {
    addToast(MESSAGES.FILE_UPLOAD.UPLOAD_FILE_ERROR, "error");
    setFileUploadStatus(Status.ERROR);
    setFile(null);
  };

  const handleRetryUpload = (): void => {
    const fileType = activeTab.title as FileOptionTabTitle;
    // Clear validation state
    setFailedValidationResponses((prev) => {
      const updated = { ...prev };
      delete updated[fileType];
      return updated;
    });
    setLastUploadedFilenames((prev) => {
      const updated = { ...prev };
      delete updated[fileType];
      return updated;
    });
    // Clear previously uploaded file from state
    setUploadedFiles((prev: UploadedFiles) => {
      const updated = { ...prev };
      delete updated[fileType];
      return updated;
    });
    triggerFileInput();
  };

  // Callback for when validation fails in the modal
  const handleValidationFailed = (response: ValidationResponse): void => {
    const fileType = activeTab.title as FileOptionTabTitle;
    setFailedValidationResponses((prev) => ({
      ...prev,
      [fileType]: response,
    }));
  };

  const handleFile = async (file: File): Promise<void> => {
    if (validateFile(file, addToast)) {
      // Reset any previous validation state for this file type
      const fileType = activeTab.title as FileOptionTabTitle;
      setFailedValidationResponses((prev) => {
        const updated = { ...prev };
        delete updated[fileType];
        return updated;
      });
      setLastUploadedFilenames((prev) => ({
        ...prev,
        [fileType]: file.name,
      }));
      setFileUploadStatus(Status.LOADING);
      setFile(file);
    }
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const files = event.dataTransfer.files;

    if (files.length > 1) {
      addToast(MESSAGES.FILE_DROP.SINGLE_FILE_ONLY, "error");
      return;
    }

    handleFile(files[0]);
  };

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  return (
    <BackgroundContainer>
      {roleConfig.showSideMenu && (
        <Column flex={show ? "0 0 15%" : "0 0 0%"} show={show}>
          <SideMenu show={show} setShow={setShow} />
        </Column>
      )}
      <Column flex="1" bg="#F5F5F5" overflow={true}>
        <NavBarContainer>
          <RowContainer mt={1} mb={1}>
            <NavBar address={`add-file / ${slugify(activeTab.title)}`} />
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={-15}>
            <CompassMainHeader pt={1} ml={12}>
              {`Add ${activeTab.title}`}
            </CompassMainHeader>
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={1}>
            <CompassParagraph ml={20} color={greyText}>
              {MESSAGES.FILE_UPLOAD.ADD_FILE_TITLE_DESCRIPTION}
            </CompassParagraph>
          </RowContainer>
          {roleConfig.showMagicLinkButton && (
            <RowContainer ai="center" jc="flex-end">
              <ElementContainer mr={10}>
                <PageButton
                  content={
                    <CompassParagraph cursor="pointer">
                      {MESSAGES.MAGIC_LINK.CREATE_BUTTON}
                    </CompassParagraph>
                  }
                  image={linkIcon}
                  action={(): void => setShowMagicLinkModal(true)}
                  he={true}
                />
              </ElementContainer>
            </RowContainer>
          )}
        </NavBarContainer>
        <RowContainer ai={"flex-start"} jc={"flex-start"} mt={-1}>
          <Card
            h={"75vh"}
            p="8px"
            style={{ minHeight: "75vh", height: "auto" }}
          >
            {roleConfig.showFileTabs && (
              <RowContainer
                mt={-1}
                jc="flex-start"
                ai="center"
                style={{ alignItems: "center" }}
              >
                <TabOptions
                  tabElements={tabElements}
                  setTabElements={setTabElements}
                  override={overrideFileType}
                />
              </RowContainer>
            )}
            <FlexibleContainer fg={1} fd="column" p="12px">
              {/* Only show upload instructions when no file uploaded and no validation errors */}
              {!failedValidationResponses[
                activeTab.title as FileOptionTabTitle
              ] &&
                !uploadedFiles[activeTab.title as FileOptionTabTitle]
                  ?.location && (
                  <RowContainer jc="center">
                    <CompassParagraph color={greyText}>
                      {MESSAGES.FILE_UPLOAD.ADD_FILE_MAIN}
                    </CompassParagraph>
                  </RowContainer>
                )}
              {/* Hide upload box when showing validation errors or success */}
              {!failedValidationResponses[
                activeTab.title as FileOptionTabTitle
              ] &&
                !uploadedFiles[activeTab.title as FileOptionTabTitle]
                  ?.location && (
                  <BoxWithShadow
                    onClick={triggerFileInput}
                    onDrop={handleFileDrop}
                    onDragOver={handleDragOver}
                    onDragEnter={(): void => setIsDragOver(true)}
                    onDragLeave={(): void => setIsDragOver(false)}
                  >
                    <AddFileIconContainer
                      size={100}
                      m={20}
                      isDragOver={isDragOver}
                    >
                      <Icon src={fileUploadIcon} />
                    </AddFileIconContainer>
                    <ElementContainer>
                      <CompassParagraph color={compassLightGreen} mr={5}>
                        {MESSAGES.FILE_DROP.CLICK_TO_UPLOAD}
                      </CompassParagraph>
                      <CompassParagraph mr={5}>
                        {MESSAGES.FILE_DROP.DRAG_AND_DROP}
                      </CompassParagraph>
                      <CompassParagraph mr={5}>
                        {activeTab.title.toLowerCase()}
                      </CompassParagraph>
                      <CompassParagraph>
                        {MESSAGES.FILE_DROP.HERE}
                      </CompassParagraph>
                    </ElementContainer>
                    <CompassParagraph fontSize={12} mt={10}>
                      {MESSAGES.FILE_DROP.FILE_TYPES}
                    </CompassParagraph>
                    <HiddenFileInput
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".xlsx, .csv"
                    />
                  </BoxWithShadow>
                )}
              <RowContainer jc="flex-start">
                <FileLoadingCard file={file} status={fileUploadStatus} />
              </RowContainer>
              {/* Show validation failed box */}
              {failedValidationResponses[
                activeTab.title as FileOptionTabTitle
              ] &&
                !failedValidationResponses[
                  activeTab.title as FileOptionTabTitle
                ]?.validation_passed && (
                  <FailedUploadBox
                    validationResponse={
                      failedValidationResponses[
                        activeTab.title as FileOptionTabTitle
                      ] as ValidationResponse
                    }
                    filename={
                      lastUploadedFilenames[
                        activeTab.title as FileOptionTabTitle
                      ] || ""
                    }
                    onRetry={handleRetryUpload}
                  />
                )}
              {/* Show success box - file uploaded, ready for validation & job */}
              {uploadedFiles[activeTab.title as FileOptionTabTitle]?.location &&
                !failedValidationResponses[
                  activeTab.title as FileOptionTabTitle
                ] && (
                  <SuccessFulUploadBox
                    fileType={activeTab.title as FileOptionTabTitle}
                    onValidationFailed={handleValidationFailed}
                  />
                )}
            </FlexibleContainer>
          </Card>
        </RowContainer>
      </Column>

      <MagicLinkModal
        show={showMagicLinkModal}
        handleClose={(): void => setShowMagicLinkModal(false)}
      />
    </BackgroundContainer>
  );
};
